import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NavWrapper = styled.div`
  position: fixed;
  bottom: 30px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  pointer-events: none;
`;

const NavContainer = styled(motion.nav)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  padding: 15px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  max-width: 90%;
  min-width: 500px;
  pointer-events: auto;
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  gap: 30px;
  justify-content: center;
  margin: 0;
  padding: 0;
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 5px 0;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    color: #f0f0f0;
    transform: translateY(-2px);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background-color: #fff;
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const Divider = styled.div`
  height: 20px;
  width: 1px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 5px;
`;

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  margin-left: 30px;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
  padding-left: 20px;
`;

const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  color: #fff;
  opacity: ${props => (props.active ? '1' : '0.5')};
  transition: all 0.3s ease;
  margin: 0 8px;
  font-size: 0.8rem;

  &:hover {
    opacity: 1;
    transform: translateY(-2px);
  }
`;

const Flag = styled.span`
  margin-right: 5px;
  font-size: 1.2rem;
`;

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  
  // Vérifier si nous sommes sur la page d'accueil
  const isHomePage = location.pathname === '/';

  // Effet pour gérer les changements de page
  useEffect(() => {
    // Masquer d'abord la navigation
    setVisible(false);
    
    // Pour la page d'accueil, gérer la visibilité en fonction du défilement
    if (isHomePage) {
      const handleScroll = () => {
        if (window.scrollY > window.innerHeight * 0.5) {
          setVisible(true);
        } else {
          setVisible(false);
        }
      };

      // Vérifier initialement
      handleScroll();
      
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      // Sur les autres pages, montrer après un délai
      console.log("Programmation du délai d'apparition...");
      const timer = setTimeout(() => {
        console.log("Fin du délai - Affichage de la navigation");
        setVisible(true);
      }, 2000); // Délai de 4 secondes
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname, isHomePage]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const currentLanguage = i18n.language;
  const currentPath = location.pathname;

  // Définir les options de navigation en fonction de la page active
  const getNavigationItems = () => {
    // Définition des éléments de navigation avec des libellés améliorés
    const allItems = [
      { path: '/', label: t('navigation.mainPage') },
      { path: '/documentation', label: t('navigation.documentation') },
      { path: '/about', label: t('navigation.about') },
      { path: '/contact', label: t('navigation.contact') }
    ];

    // Filtrer l'élément actif
    return allItems.filter(item => {
      // Cas spécial pour les pages de documentation
      if (currentPath.startsWith('/documentation/') && item.path === '/documentation') {
        return false;
      }
      return item.path !== currentPath;
    });
  };

  console.log("État actuel de visible:", visible, "Page d'accueil:", isHomePage, "Chemin:", location.pathname);

  return (
    <AnimatePresence>
      {visible && (
        <NavWrapper>
          <NavContainer
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ 
              duration: 0.5, 
              type: 'spring', 
              stiffness: 100 
            }}
          >
            <NavList>
              {getNavigationItems().map((item, index) => (
                <NavItem key={index}>
                  <NavLink to={item.path}>{item.label}</NavLink>
                </NavItem>
              ))}
            </NavList>

            <LanguageSelector>
              <LanguageOption 
                active={currentLanguage === 'en'} 
                onClick={() => changeLanguage('en')}
              >
                <Flag>🇺🇸</Flag> {t('language.english')}
              </LanguageOption>
              <Divider />
              <LanguageOption 
                active={currentLanguage === 'zh'} 
                onClick={() => changeLanguage('zh')}
              >
                <Flag>🇨🇳</Flag> {t('language.mandarin')}
              </LanguageOption>
            </LanguageSelector>
          </NavContainer>
        </NavWrapper>
      )}
    </AnimatePresence>
  );
};

export default Navigation;